
'use client';

import Image from 'next/image';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function ArtisticEditorialHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-stone-100 text-stone-900 font-serif">
      <header className="relative p-6 md:p-8 z-20 flex justify-between items-center bg-stone-100 border-b border-stone-200">
        <h2 className="text-xl md:text-2xl font-bold text-stone-800 tracking-wider">
          Aperture
        </h2>
        <nav className="hidden md:flex gap-6 text-sm font-bold tracking-widest uppercase">
          <a href="#" className="text-stone-800">
            The Journal
          </a>
          <a href="#" className="text-stone-800">
            About
          </a>
          <a href="#" className="text-stone-800">
            Subscribe
          </a>
        </nav>
        <button
          className="md:hidden text-stone-800 text-sm font-bold tracking-widest uppercase"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : 'Menu'}
        </button>
      </header>

      {isMenuOpen && (
        <div className="fixed inset-0 bg-stone-900/95 z-30 flex items-center justify-center md:hidden">
          <button
            className="absolute top-6 right-6 text-white"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={32} />
          </button>
          <nav className="flex flex-col gap-8 text-3xl text-center text-white font-bold tracking-widest uppercase">
            <a href="#" onClick={() => setIsMenuOpen(false)}>
              The Journal
            </a>
            <a href="#" onClick={() => setIsMenuOpen(false)}>
              About
            </a>
            <a href="#" onClick={() => setIsMenuOpen(false)}>
              Subscribe
            </a>
          </nav>
        </div>
      )}

      <main>
        <section className="h-screen relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-10 w-full px-4">
            <h1 className="text-6xl md:text-9xl font-bold text-white tracking-tighter">
              The Issue
            </h1>
            <p className="text-white mt-2 text-lg">Nº 04 — The Art of Seeing</p>
          </div>
          <Image
            src="https://picsum.photos/seed/art1/1800/1200"
            alt="An abstract, grainy, black and white photo of a person's silhouette"
            fill
            className="object-cover"
            priority
            data-ai-hint="abstract silhouette"
          />
        </section>

        <section className="py-16 md:py-24 px-8 container mx-auto">
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                In This Issue
              </h2>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12">
              <div className="col-span-1">
                <p className="text-sm uppercase tracking-widest text-stone-500 mb-2">
                  01 / Featured
                </p>
                <h3 className="text-2xl font-semibold mb-2">
                  A Conversation with a Visionary
                </h3>
                <p className="text-stone-600">
                  We sit down with artist Eva Rostova to discuss her process,
                  inspiration, and the future of her craft.
                </p>
              </div>
              <div className="col-span-1">
                <p className="text-sm uppercase tracking-widest text-stone-500 mb-2">
                  02 / Profile
                </p>
                <h3 className="text-2xl font-semibold mb-2">
                  The Concrete Jungle
                </h3>
                <p className="text-stone-600">
                  Photographer Kenji Tanaka captures the brutalist beauty of
                  urban landscapes.
                </p>
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm uppercase tracking-widest text-stone-500 mb-2">
                  03 / Photo Essay
                </p>
                <h3 className="text-2xl font-semibold mb-2">Ephemeral Light</h3>
                <p className="text-stone-600">
                  A visual journey through the fleeting moments of dawn and
                  dusk, captured on film.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full relative h-[70vh]">
            <Image
              src="https://picsum.photos/seed/art-mid/1800/1000"
              alt="Wide landscape shot of a misty mountain range"
              fill
              className="object-cover"
              data-ai-hint="misty mountains"
            />
          </div>
          <div className="container mx-auto px-8 mt-16 grid md:grid-cols-12 gap-8">
            <div className="md:col-span-8 md:col-start-3 text-center">
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                "The camera is an instrument that teaches people how to see
                without a camera."
              </h3>
              <p className="mt-4 text-stone-500">- Dorothea Lange</p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 px-8 container mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div className="relative h-96">
              <Image
                src="https://picsum.photos/seed/art-gal1/800/1000"
                alt="Close up shot of a painter's hands covered in paint"
                fill
                className="object-cover"
                data-ai-hint="painter hands"
              />
            </div>
            <div className="relative h-96">
              <Image
                src="https://picsum.photos/seed/art-gal2/800/1000"
                alt="A minimalist sculpture in a stark white room"
                fill
                className="object-cover"
                data-ai-hint="minimalist sculpture"
              />
            </div>
            <div className="relative h-96 sm:col-span-2 md:col-span-1">
              <Image
                src="https://picsum.photos/seed/art-gal3/800/1000"
                alt="A dancer in mid-motion, blurred and abstract"
                fill
                className="object-cover"
                data-ai-hint="dancer motion"
              />
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-stone-900 text-stone-100 py-16 px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-3xl font-bold tracking-tighter">
                Subscribe to Aperture
              </h3>
              <p className="text-stone-400 mt-2">
                Receive our quarterly journal and exclusive digital content.
              </p>
            </div>
            <div className="flex items-center">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full bg-stone-800 border border-stone-700 p-3 placeholder-stone-500"
              />
              <button className="bg-stone-100 text-stone-900 p-3 h-full">
                <ArrowRight />
              </button>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center text-sm text-stone-500 gap-4">
            <p>&copy; {new Date().getFullYear()} Aperture Journal</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white">
                Instagram
              </a>
              <a href="#" className="hover:text-white">
                Submissions
              </a>
              <a href="#" className="hover:text-white">
                Stockists
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
